import { useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { attachmentError, formatFileSize } from '../../domain/attachment'
import { errorMessage } from '../../lib/errors'
import { formatAuditTimestamp } from '../../lib/time'
import { useAddTaskUpdate, useTaskHistory } from './task.queries'
import { getAttachmentDownloadUrl } from './task.repository'

export function TaskHistory({ taskId }: { taskId: string }) {
  const history = useTaskHistory(taskId)
  const addUpdate = useAddTaskUpdate(taskId)
  const [note, setNote] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState('')
  const [downloadError, setDownloadError] = useState('')
  const [openingPath, setOpeningPath] = useState('')
  const [operationId, setOperationId] = useState(() => crypto.randomUUID())
  const fileInput = useRef<HTMLInputElement>(null)

  function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(event.target.files ?? [])
    const invalid = selected.map(attachmentError).find(Boolean)
    if (invalid) {
      setFileError(invalid)
      setFiles([])
      event.target.value = ''
      return
    }
    setFileError('')
    setFiles(selected)
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!note.trim()) return
    try {
      await addUpdate.mutateAsync({ id: operationId, note, files })
      setNote('')
      setFiles([])
      setFileError('')
      if (fileInput.current) fileInput.current.value = ''
      setOperationId(crypto.randomUUID())
    } catch {
      // Keep the operation ID and content so a retry is idempotent.
    }
  }

  async function openAttachment(attachment: NonNullable<typeof history.data>[number]['attachments'][number]) {
    setDownloadError('')
    setOpeningPath(attachment.path)
    try {
      const url = await getAttachmentDownloadUrl(attachment)
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noreferrer'
      link.click()
    } catch (error) {
      setDownloadError(errorMessage(error))
    } finally {
      setOpeningPath('')
    }
  }

  return (
    <section className="card p-5 sm:p-6" aria-labelledby="history-title">
      <h2 className="text-xl font-extrabold text-slate-900" id="history-title">Andamento</h2>
      <p className="mt-1 text-sm text-slate-600">Cada anotação é permanente e recebe a hora do servidor.</p>
      <form className="mt-5 grid gap-3" onSubmit={submit}>
        <label className="field">
          <span>Novo andamento</span>
          <textarea className="control min-h-28 resize-y" maxLength={5000} value={note} onChange={(event) => setNote(event.target.value)} required />
        </label>
        <label className="field">
          <span>Anexos (opcional)</span>
          <input
            ref={fileInput}
            className="control file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:font-bold file:text-blue-700"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.xml,.zip"
            onChange={selectFiles}
          />
          <small className="text-slate-500">PDF, imagens, Word, Excel, XML ou ZIP; até 10 MiB por arquivo.</small>
        </label>
        {files.length ? (
          <ul className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
            {files.map((file) => <li key={`${file.name}-${file.size}`}>{file.name} · {formatFileSize(file.size)}</li>)}
          </ul>
        ) : null}
        {fileError ? <p className="notice-error" role="alert">{fileError}</p> : null}
        {addUpdate.isError ? <p className="notice-error" role="alert">{errorMessage(addUpdate.error)}</p> : null}
        {addUpdate.isSuccess ? <p className="notice-success" role="status">Andamento registrado.</p> : null}
        <button className="btn-primary justify-self-start" disabled={addUpdate.isPending || !note.trim()} type="submit">
          {addUpdate.isPending ? 'Registrando…' : 'Registrar andamento'}
        </button>
      </form>
      <ol className="mt-7 grid gap-4 border-l-2 border-slate-200 pl-5">
        {history.data?.map((item) => (
          <li className="relative" key={item.id}>
            <span className="absolute -left-[1.63rem] top-1 h-3 w-3 rounded-full bg-blue-600 ring-4 ring-white" aria-hidden="true" />
            <time className="text-xs font-bold uppercase tracking-wide text-slate-500">{formatAuditTimestamp(item.created_at)}</time>
            <p className="mt-1 whitespace-pre-wrap text-slate-800">{item.note}</p>
            {item.attachments.length ? (
              <ul className="mt-3 grid gap-2">
                {item.attachments.map((attachment) => (
                  <li className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5" key={attachment.path}>
                    <span className="min-w-0 truncate text-sm font-semibold text-slate-700">{attachment.name} · {formatFileSize(attachment.size)}</span>
                    <button className="btn-secondary shrink-0 text-sm" disabled={openingPath === attachment.path} onClick={() => void openAttachment(attachment)} type="button">
                      {openingPath === attachment.path ? 'Abrindo…' : 'Abrir/baixar'}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
      {downloadError ? <p className="notice-error mt-4" role="alert">{downloadError}</p> : null}
      {history.isLoading ? <p className="mt-5 text-slate-500">Carregando histórico…</p> : null}
      {history.isError ? <p className="notice-error mt-5" role="alert">{errorMessage(history.error)}</p> : null}
      {!history.isLoading && history.data?.length === 0 ? <p className="mt-5 text-slate-500">Nenhum andamento registrado.</p> : null}
    </section>
  )
}
